import * as React from 'react';
import { FixMeLater } from '../../../utils';
import { Scroller, ScrollerProps } from '../../Scroller';
import { Styler, StylerBaseProps } from '../../Styler/Styler';
import { Tab, TabProps } from '../Tab/Tab';
import { useTabs } from '../Tabs.context';
import { STATIC_STYLES, useDynamicStyles } from '../Tabs.styles';

export interface TabsListProps extends StylerBaseProps {
    /**
     * The child `tab` components
     */
    children?: React.ReactNode;

    /**
     * Additional properties applied to the Scroller component.
     */
    ScrollerProps?: ScrollerProps;
}

export const TabsList: React.ForwardRefExoticComponent<TabsListProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function TabsList(props: TabsListProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const { children, ScrollerProps: ScrollerPropsProp, ...other } = props;
        const { positioning, underline, ariaLabel, tabListRef, variant, size, color } = useTabs();

        const dynamicStyles = useDynamicStyles(size, variant, color);

        const childTabs: React.ReactElement<TabProps>[] = React.useMemo(() => {
            return React.Children.toArray(children).filter(
                (child) => React.isValidElement(child) && child.type === Tab
            ) as React.ReactElement<TabProps>[];
        }, [children]);

        return (
            <Styler
                ref={ref}
                baseStyles={[dynamicStyles, STATIC_STYLES]}
                data-tabs-root={true}
                data-positioning={positioning}
                data-underline={underline || undefined}
                {...other}
            >
                <Scroller
                    TrackProps={{ 'aria-label': ariaLabel, role: 'tablist', 'data-tab-list': true }}
                    trackRef={tabListRef}
                    showOverflowGradient={false}
                    {...ScrollerPropsProp}
                >
                    {childTabs.map((child, index) => {
                        return React.cloneElement(child, {
                            index,
                        });
                    })}
                </Scroller>
            </Styler>
        );
    });
